import { useFormContext } from 'react-hook-form';
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  Stack,
  Typography,
} from '@mui/material';
import { diagnosesApiSlice, IDiagnosesItem } from '@src/api';
import { border, buildDataTestId, buildDataTestIdAttribute, hexOpacity, scrollBarSM } from '@src/utils';
import { Autocomplete } from '@components/Autocomplete';
import { theme } from '@src/theme';
import { ReactComponent as PopupIcon } from '@icons/popupIcon.svg';
import { DiagnosesCategory, DiagnosesOptionItem } from './DiagnosesInput.components';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { useDebounce } from '@src/hooks/useDebounce';

export const PaperComponent = memo(
  ({
    children,
    search,
    handleCancel,
    dataTestId = '',
    open,
    ...paperProps
  }: PaperProps & {
    handleCancel: () => void;
    search: string;
    dataTestId?: string;
    open: boolean;
  }) => {
    const { setValue, watch } = useFormContext();
    const [diagnosisIds] = watch(['diagnosisIds']);
    const debouncedSearch = useDebounce(search);

    const [localChecked, setLocalChecked] = useState<IDiagnosesItem[]>([]);
    const [parentDiagnosisEsbId, setParentDiagnosisEsbId] = useState<Nullable<string>>(null);

    const handleApply = () => {
      setValue('diagnosisIds', localChecked, { shouldValidate: true });
      handleCancel();
    };
    useEffect(() => {
      if (diagnosisIds) setLocalChecked(diagnosisIds as IDiagnosesItem[]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { selectableDiagnoses, isLoading: isFetchingSelectable } = diagnosesApiSlice.useGetDiagnosesByEsbIdQuery(
      {
        search: debouncedSearch,
        parentDiagnosisEsbId,
        selectable: true,
      },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          selectableDiagnoses: data?.data?.filter(({ selectable }) => !!selectable) || [],
        }),
      },
    );

    const { data: unselectableDiagnoses, isLoading: isFetchingUnselectable } =
      diagnosesApiSlice.useGetUnselectableDiagnosesWithoutEbsIdQuery({
        search: debouncedSearch,
        selectable: false,
      });

    const searchIsEmpty = unselectableDiagnoses?.data?.length === 0 && selectableDiagnoses?.length === 0 && search;

    const onlySelectableDiagnoses =
      unselectableDiagnoses?.data && unselectableDiagnoses.data.length === 0 && selectableDiagnoses?.length > 0;
    return (
      <Box height="100%">
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            sx={{
              height: '100%',
              zIndex: 1,
            }}
            {...paperProps}
          >
            <Stack position="relative" overflow="auto" height="calc(100% - 55px)" sx={scrollBarSM} direction="row">
              {isFetchingUnselectable || isFetchingSelectable ? (
                <PreloaderBubbles />
              ) : searchIsEmpty ? (
                <Box padding="24px">
                  <Typography color="grey.600" variant="label12Medium">
                    Diagnosis not found. Try to change search request.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Stack
                    width={onlySelectableDiagnoses ? 0 : '50%'}
                    borderRight={border}
                    mt="12px"
                    overflow="auto"
                    sx={scrollBarSM}
                  >
                    {unselectableDiagnoses?.data?.map((diagnosis, index) => {
                      return (
                        <DiagnosesCategory
                          key={diagnosis.id}
                          diagnosis={diagnosis}
                          setParentDiagnosisEsbId={setParentDiagnosisEsbId}
                          lastItemCategoryDiagnosis={null}
                          parentDiagnosisEsbId={parentDiagnosisEsbId}
                          level={1}
                          dataTestId={buildDataTestId(dataTestId, `children-${index}`)}
                        />
                      );
                    })}
                  </Stack>
                  <Stack width={onlySelectableDiagnoses ? '100%' : '50%'} overflow="auto" sx={scrollBarSM}>
                    {selectableDiagnoses?.map(diagnosis => {
                      const checked = !!localChecked.some(({ id }) => id === diagnosis.id);

                      const onClickDiagnosis = () => {
                        setLocalChecked(prev => {
                          const { name, id, description } = diagnosis;
                          const isAlreadyChecked = prev.some(d => d.id === diagnosis.id);
                          if (!isAlreadyChecked) {
                            return [...prev, { id, name, description, selectable: true }];
                          }
                          return prev.filter(({ id: checkedId }) => id !== checkedId);
                        });
                      };
                      return (
                        <DiagnosesOptionItem
                          key={diagnosis.id}
                          diagnosis={diagnosis}
                          checked={checked}
                          onClick={onClickDiagnosis}
                          disabled={false}
                        />
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>

            <Stack
              padding="12px"
              direction="row"
              justifyContent="space-between"
              position="absolute"
              bottom="0"
              width="100%"
              borderTop={`1px solid ${hexOpacity(theme.palette.secondary.main, '14%')}`}
              zIndex={20}
              bgcolor="#FFFFFF"
              boxSizing="border-box"
            >
              <Button
                disabled={false}
                color="secondary"
                size="small"
                variant="outlined"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleCancel}
                {...buildDataTestIdAttribute(dataTestId, 'button-cancel')}
              >
                Cancel
              </Button>

              <Button
                color="secondary"
                variant="contained"
                size="small"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleApply}
                {...buildDataTestIdAttribute(dataTestId, 'button-apply')}
              >
                Apply
              </Button>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Box>
    );
  },
);

export const DiagnosesInput = memo(() => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const customPopper = useMemo(
    () =>
      ({ children, ...popperProps }: PopperProps) => {
        return (
          <Popper
            sx={{
              height: '354px',
            }}
            {...popperProps}
            modifiers={[
              {
                name: 'flip',
                options: {
                  allowedAutoPlacements: ['top', 'bottom'],
                },
              },
              {
                name: 'offset',
                options: {
                  offset: [0, 2],
                },
              },
            ]}
            placement="bottom"
          >
            {children}
          </Popper>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleChange = (_e: ChangeEvent<HTMLInputElement>, value: string, reason) => {
    if (reason === 'blur' || reason === 'reset') return;
    setSearch(value || '');
  };
  const resultDataTestId = buildDataTestId('create-order-modal', 'input-tests-search');

  const handleCancel = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, [setSearch, setOpen]);

  return (
    <Autocomplete
      showCustomClearIcon
      renderOption={() => <></>}
      open={open}
      onClose={() => {}}
      PaperComponent={(props: PaperProps) => (
        <PaperComponent
          {...props}
          handleCancel={handleCancel}
          search={search}
          dataTestId={resultDataTestId}
          open={open}
        />
      )}
      PopperComponent={customPopper}
      setState={{}}
      onClick={() => setOpen(true)}
      componentsProps={{ popper: { sx: { zIndex: 1 } } }}
      placeholder="Add additional diagnoses"
      options={[]}
      popupIcon={<PopupIcon />}
      multiple={undefined}
      disabled={undefined}
      value={undefined}
      loading={undefined}
      disableClearable={true}
      inputIsClearable={false}
      onChange={() => {}}
      withSearchIcon={false}
      sx={{ '& .MuiFormControl-root': { '& .MuiInputBase-root': { padding: '12px', input: { height: '18px' } } } }}
      onInputChange={handleChange}
      inputValue={search}
      dataTestId={resultDataTestId}
    />
  );
});
